import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from '@shared/core';
import { useSendGuestIdentificationEmailMutation } from '../GuestIdentification.graphql.generated';
import { addError } from '@shared/utils/logger';
import type { EmailStepFields } from '../GuestIdentification.types';

type UseEmailStepControllerProps = Readonly<{
  eventId: string;
  onCodeSent: (email: string) => void;
}>;

export const useEmailStepController = ({ eventId, onCodeSent }: UseEmailStepControllerProps) => {
  const { tq } = useTranslation('guestUnlock');
  const [sendCode] = useSendGuestIdentificationEmailMutation();

  const formik = useFormik<EmailStepFields>({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object<EmailStepFields>({
      email: Yup.string()
        .trim()
        .email(tq`Please enter a valid email`)
        .required(tq`Email is required`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      const trimmedEmail = values.email.trim();
      try {
        // The backend intentionally returns `success: true` regardless of whether the email
        // matched a guest (anti-enumeration). We always advance to the OTP step — if no code
        // was sent, the guest will simply fail verification and land on the error state.
        await sendCode({ variables: { eventId, email: trimmedEmail } });
        onCodeSent(trimmedEmail);
      } catch (error) {
        addError(error, { context: 'EmailStep/sendGuestIdentificationEmail', eventId });
        // Advance anyway so the UX remains consistent with the anti-enumeration contract.
        onCodeSent(trimmedEmail);
      }
    },
  });

  return {
    formik,
    loading: formik.isSubmitting,
  };
};
